import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { User } from "@supabase/supabase-js";

import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { supabase } from "@/integrations/supabase/client";
import { PENDING_PLAN_KEY } from "@/lib/plans";
import { visibleTiers as tiers } from "@/content/pricing";
import { BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/checkout")({
  validateSearch: (search: Record<string, unknown>): { plan?: string } =>
    typeof search["plan"] === "string" ? { plan: search["plan"] } : {},
  head: () => ({
    meta: [
      { title: "Secure checkout | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content: "Complete your Smart Word Labs enrolment securely.",
      },
      { property: "og:title", content: "Secure checkout | Smart Word Labs" },
      {
        property: "og:description",
        content: "Complete your Smart Word Labs enrolment securely.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
  }),
  component: CheckoutPage,
});

function CheckoutPage() {
  const { t } = useTranslation("pages");
  const { plan } = Route.useSearch();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  const tier = tiers.find((t) => t.priceId === plan);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user ?? null);
      setReady(true);
      if (!data.user && plan) {
        try {
          sessionStorage.setItem(PENDING_PLAN_KEY, plan);
        } catch {
          /* storage unavailable */
        }
        navigate({ to: "/auth", search: { mode: "signup" } });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  return (
    <main className="mx-auto max-w-3xl px-5 py-16">
      <h1 className="text-3xl text-primary">{t("checkout.title")}</h1>
      {tier ? (
        <p className="mt-2 text-muted-foreground">
          {t(tier.oneTime ? "checkout.summaryOneTime" : "checkout.summary", { name: tier.name, monthly: tier.monthly, lessons: tier.lessons })}
        </p>
      ) : (
        <p className="mt-2 text-muted-foreground">
          {t("checkout.choosePlan.before")}{" "}
          <Link to="/pricing" className="underline">
            {t("checkout.choosePlan.link")}
          </Link>{" "}
          {t("checkout.choosePlan.after")}
        </p>
      )}

      {ready && user && tier && (
        <div className="mt-8">
          <StripeEmbeddedCheckout
            priceId={tier.priceId}
            quantity={1}
            {...(user.email && { customerEmail: user.email })}
            userId={user.id}
            returnUrl={`${window.location.origin}/dashboard?checkout=success`}
          />
        </div>
      )}
    </main>
  );
}
